//
// DESCRIPTION
//
// Instances of the FormState object represent a form's current state
// independently from React or Redux.
//
// A FormState instance contains the state currently edited attributes
// and handles validation. It uses transformator objects to convert
// model/resource data into serialized, form-editable representations,
// and back. It also uses validator objects to control the validation
// for each form attribute.
//
// The formState has the optional feature of groups allowing you to
// export and validate groups of attributes separately from each other
//
// To integrate the formState with React, pass the formState into
// each form input component, then read and write the respective
// attribute and its validation state (see FsInput.jsx for an example)
// The formState also expects a way to re-render the specific component
// by assigning a formState.renderCallback() (which in React should
// change the components state or call the components forceUpdate())
//
// An external server validation result can also be passed into the
// formState so that it can be displayed in a similar manner as the client
// validations

//
// USAGE - CREATION
//
// myForm = Object.create(FormState).init({
//   attributeMapping: {
//     attributeName1: { validator: someValidator.isValid, transformator: someTransformator }
//     attributeName2: { validator: someOtherValidator.isValidAndOptional }
//   },
//   model: this.props.myModel,
// })
//
// Other popular options:
//
// - renderCallback: Tell formState how to rerender (e.g. with react)
// - serverValidation: Initial serverValidation, can also be set later with updateServerValidation
// - groups: Keys are group names, values are lists of attributes of this group
//
// USAGE - API
//
// From Form Field
//
// myForm.readFormAttribute(attributeName): Use this from your form components
// myForm.readFormAttributeValidation(attributeName): Use this from your form components
// myForm.updateFormAttribute(attributeName, newValue): Use this from your form components
//
// Transform to Model
//
// myForm.readModel(): Export to model data (e.g. for API)
// myForm.readModelGroup(groupName): Export to model data (e.g. for API)
// myForm.readModelAttribute(attributeName): Export to model data (e.g. for API)
//
// Validation
//
// myForm.clientValidateAll(): Validate all attributes (might affect validation mode)
// myForm.clientValidateGroup(groupName): Validate only given group (might affect validation mode)
// myForm.clientValidateAttribute(attributeName): Validate attribute (might affect validation mode)
// myForm.serverValidate(validationMapping): Update serverValidation (changes validation mode)
//
// ALSO SEE
//
// FormState.test.jsx
// FsInput.jsx
// Example: OrderUnitEditor.jsx
// Example: RegistrationForm.jsx
//
// More information about OLOO style
// https://github.com/getify/You-Dont-Know-JS/blob/master/this%20%26%20object%20prototypes/ch6.md

import { defaultTextValidator } from "shared/validators";
import { defaultValueTransformator } from "shared/transformators";

const defaultValidationOptions = {
  validationModeChange: true,
};

const FormState = {
  // // // INITIALIZATION

  // Must be called see above for documentation
  init(configuration) {
    if (!configuration) {
      throw new Error(
        "FormState needs a configuration (with at least one attributeConfig entry",
      );
    }

    // init indirect internal vars

    this.initialized = true;
    this.isSubmitButtonDisabled = false;
    this.knownAttributes = [];
    this.transformators = {};
    this.validators = {};
    this.model = {};
    this.state = {};
    this.validationModes = {};
    this.renderCallback = () => {};

    this.serverValidationState = configuration.serverValidation || {};
    this.groups = configuration.groups || {};

    // apply configuration & set model

    this.initializeAttributeMapping(configuration.attributeMapping);
    this.updateModel(configuration.model || {});

    // hook up react or similar
    this.renderCallback = configuration.renderCallback || (() => {});

    return this;
  },

  initializeAttributeMapping(attributeMapping) {
    if (!attributeMapping) {
      throw new Error("attributeMapping must be supplied");
    }

    const attributeEntries = Object.entries(attributeMapping);

    if (attributeEntries.length === 0) {
      throw new Error("attributeMapping must contain at least one entry");
    }

    attributeEntries.forEach(([attributeName, attributeConfig]) => {
      this.knownAttributes.push(attributeName);

      if (attributeConfig === true) {
        this.transformators[attributeName] = defaultValueTransformator;
        this.validators[attributeName] = defaultTextValidator.isValid;
      } else {
        this.transformators[attributeName] =
          attributeConfig.transformator || defaultValueTransformator;
        this.validators[attributeName] =
          attributeConfig.validator || defaultTextValidator.isValid;
      }
    });
  },

  // // // PUBLIC WRITING API (some are used in initializer)

  updateModel(modelObject) {
    if (!modelObject) {
      return;
    }

    this.state = this.knownAttributes.reduce((state, attributeName) => {
      const modelAttributeValue = modelObject[attributeName];
      const formAttributeValue =
        this.transformators[attributeName].toFormAttribute(modelAttributeValue);
      return { ...state, [attributeName]: formAttributeValue };
    }, {});

    this.renderCallback();
  },

  updateServerValidation(errors) {
    this.serverValidationState = {};
    if (!errors) {
      return;
    }

    this.serverValidationState = errors.reduce(
      (serverValidationState, error) => {
        return { ...serverValidationState, [error.attribute]: false };
      },
      {},
    );

    this.validationModes = errors.reduce((validationModes, error) => {
      return { ...validationModes, [error.attribute]: "SERVER" };
    }, {});

    this.renderCallback();
  },

  updateFormAttribute(attributeName, newValue) {
    const oldValue = this.state[attributeName];
    this.state[attributeName] = newValue;

    if (
      newValue !== oldValue &&
      this.readValidationMode(attributeName) === "SERVER"
    ) {
      this.validationModes[attributeName] = "INSTANT";
    }

    this.renderCallback();
  },

  // // // PUBLIC READING API

  // Transform internal state to model representation
  readModel() {
    return Object.entries(this.state).reduce(
      (accumulator, [attributeName, formAttributeValue]) => {
        return {
          ...accumulator,
          [attributeName]:
            this.transformators[attributeName].toModelAttribute(
              formAttributeValue,
            ),
        };
      },
      {},
    );
  },

  // Transform internal state to model representation (only one group)
  readModelGroup(groupName) {
    return this.groups[groupName].reduce((accumulator, attributeName) => {
      const formAttributeValue = this.state[attributeName];
      return {
        ...accumulator,
        [attributeName]:
          this.transformators[attributeName].toModelAttribute(
            formAttributeValue,
          ),
      };
    }, {});
  },

  // Transform internal state to model representation (single attribute)
  readModelAttribute(attributeName) {
    const formAttributeValue = this.state[attributeName];
    return this.transformators[attributeName].toModelAttribute(
      formAttributeValue,
    );
  },

  // Return form field representation of attribute
  readFormAttribute(attributeName) {
    return this.state[attributeName];
  },

  // Return validation of attribute: First try server map, then live client validation
  readFormAttributeValidation(attributeName) {
    if (
      this.readValidationMode(attributeName) === "SERVER" &&
      this.serverValidationState[attributeName] === false
    ) {
      return false;
    }
    return this.validators[attributeName](this.state[attributeName]);
  },

  // Validation modes: "INSTANT", "SERVER", undefined (= new)
  readValidationMode(attributeName) {
    return this.validationModes[attributeName];
  },

  // Require validation of a single attribute, should be called manually before submitting,
  // affects form's validation mode (= can trigger a rerender)
  clientValidateAttribute(attributeName, options = defaultValidationOptions) {
    const attributeValid = this.validators[attributeName](
      this.state[attributeName],
    );

    if (
      options.validationModeChange &&
      this.readValidationMode(attributeName) !== "INSTANT"
    ) {
      this.validationModes[attributeName] = "INSTANT";
      this.renderCallback();
    }

    return attributeValid;
  },

  // Require validation of attribute group, should be called manually before submitting,
  // affects form's validation mode (= can trigger a rerender)
  clientValidateGroup(groupName, options = defaultValidationOptions) {
    return this.groups[groupName].reduce((isValid, attributeName) => {
      return this.clientValidateAttribute(attributeName, options) && isValid;
    }, true);
  },

  // Require validation of all attributes, should be called manually before submitting,
  // affects form's validation mode (= can trigger a rerender)
  clientValidateAll(options = defaultValidationOptions) {
    return this.knownAttributes.reduce((isValid, attributeName) => {
      return this.clientValidateAttribute(attributeName, options) && isValid;
    }, true);
  },

  // Returns validation state, but only if not all validationModes are still untouched
  // In the future, this will need group support
  // CANNOT trigger a change of validation mode / rerender
  hasValidationErrors() {
    const hasNonNewValidationMode = this.knownAttributes.some(attributeName => {
      return this.validationModes[attributeName];
    });

    return (
      hasNonNewValidationMode &&
      !this.clientValidateAll({
        validationModeChange: false,
      })
    );
  },

  // TODO Bring back such a method to potentially be able to read error messages
  // readAllClientErrors() {
  //   return Object.entries(this.clientValidationState)
  //     .filter(([_, y]) => !y).reduce((o, v) => ({ ...o, [v[0]]: v[1] }), {})
  // },
};

export default FormState;
